import { createSlice } from '@reduxjs/toolkit'
import { fetchWrapper } from '../../helper/utils'
import { setError } from '../error/errorSlice'
import { setLoader } from '../loader/loaderSlice'
import { setAnalyticsUserId } from '../../analytics/common'
import { setAmplitudeUserProperties } from '../../analytics/amplitude'
import { v4 as uuidv4 } from 'uuid';
import { setEventData } from '../events/eventsSlice';
import { setName } from '../pairs/pairsSlice';
import Cookies from 'js-cookie';

const {
  REACT_APP_API_URL,
  REACT_APP_STRIPE_PK,
  REACT_APP_STRIPE_NAME,
} = process.env;

export const signupSlice = createSlice({
  name: 'signup',
  initialState: {
    email: '',
    userName: '',
    isValid: false,
    token: null,
    gzUserID: null,
    isSuccess: null,
    userUuid: null,
    stripePublishKey: null,
    stripeAccountName: null,
    deeplinkToken: '',
    deeplinkUrl: '',
    deeplinkQR: '',
    btToken: null,
    paymentSettingsStatus: null,
    paymentProvider: null,
  },
  reducers: {
    setEmail: (state, action) => {
      state.email = action.payload.email;
      state.isValid = action.payload.isValid
    },
    setRequestResult: (state, action) => {
      const {
        access_token,
        deeplink_token,
        entity: {
          external_user_id,
          name,
          registration_source,
          email,
        },
      } = action.payload;
      state.token = access_token;
      state.isSuccess = true;
      state.gzUserID = external_user_id;
      state.userName = name === email ? '' : name;
      state.email = email;
      state.registrationSource = registration_source
      state.deeplinkToken = deeplink_token;
    },
    setUserToken: (state, action) => {
      state.token = action.payload
    },
    setUserUuid: (state, action) => {
      state.userUuid = action.payload
    },
    setStripeData: (state, action) => {
      state.stripePublishKey = action.payload.stripePublishKey;
      state.stripeAccountName = action.payload.stripeAccountName
    },
    setBtToken: (state, action) => {
      state.btToken = action.payload;
    },
    setPaymentSettingStatus: (state, action) => {
      state.paymentSettingsStatus = action.payload;
    },
    setPaymentProvider: (state, action) => {
      state.paymentProvider = action.payload;
    },
    setDeeplinkData: (state, action) => {
      const { short_deeplink, qr_code } = action.payload;
      state.deeplinkUrl = short_deeplink;
      state.deeplinkQR = qr_code;
    }
  },
});

export const {
  setEmail,
  setRequestResult,
  setUserToken,
  setUserUuid,
  setStripeData,
  setBtToken,
  setPaymentSettingStatus,
  setPaymentProvider,
  setDeeplinkData,
} = signupSlice.actions;

export const sendAPIRequest = (email, name, userUuid) => (dispatch) => {
  dispatch(setLoader(true));

  const params = {
    registration_source: 'email',
    name: name || email,
    email,
    user_uuid: userUuid,
  };

  fetchWrapper(`${REACT_APP_API_URL}/signup`, { body: params })
    .then((data) => {
      window.sessionStorage.setItem('access_token', data.data.access_token);
      dispatch(setRequestResult(data.data));
      dispatch(getPaymentSettings(data.data.access_token));
      dispatch(getDeeplinkData(data.data.access_token));
      setAnalyticsUserId(data.data.entity.external_user_id)
    })
    .catch((error) => {
      dispatch(
        setError({
          show: true,
          text: error.errors?.fields?.email || error.errors?.messages || 'Failed to create an account',
          type: 'signup',
        })
      )
      // dispatch(setRequestResult({isSuccess: false}));
    })
    .then(() => dispatch(setLoader(false)))
};

export const getDeeplinkData = (token) => (dispatch) => {
	fetchWrapper(`${REACT_APP_API_URL}/users/me/deeplink-qr-code`, {
		headers: { Authorization: `Bearer ${token}` },
	})
	.then((data) => {
		dispatch(setDeeplinkData(data.data));
	})
	.catch((error) => {
		console.error(error);
	});
}

export const getPaymentSettings = (token, attemptNum = 0) => (dispatch) => {
  let stripeParams = {
      stripePublishKey: REACT_APP_STRIPE_PK,
      stripeAccountName: REACT_APP_STRIPE_NAME,
    },
    status = null,
    paymentGateway = null;

  dispatch(setPaymentSettingStatus(status));

  fetchWrapper(`${REACT_APP_API_URL}/users/me/payments-settings`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((data) => {
      const {
        stripe_publishable_key,
        stripe_account_name,
        braintree_client_token,
        payment_gateway,
      } = data;

      paymentGateway = braintree_client_token && payment_gateway ? payment_gateway : 'stripe';
      dispatch(setBtToken(braintree_client_token || null));
      status = 'success';

      if (data.experiments?.force_3ds_for_eu_customers) {
        setAmplitudeUserProperties({ '3Ds_test': data.experiments?.force_3ds_for_eu_customers || null });
      }
      if (stripe_publishable_key) {
        stripeParams = {
          stripePublishKey: stripe_publishable_key,
          stripeAccountName: stripe_account_name,
        }
      }
    })
    .catch((error) => {
      console.log(error);
      if (attemptNum < 2) {
        setTimeout(() => dispatch(getPaymentSettings(token, ++attemptNum)), 1000);
      } else {
        status = 'fail';
        paymentGateway = 'stripe';
      }
    })
    .then(() => {
      dispatch(setPaymentProvider(paymentGateway));
      dispatch(setStripeData(stripeParams));
      dispatch(setPaymentSettingStatus(status));
    })
};

export const getUserInfo = (token) => (dispatch) => {
  fetchWrapper(`${REACT_APP_API_URL}/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((data) => {
      window.sessionStorage.setItem('access_token', data.access_token);
      dispatch(setUuid(data.data.entity.uuid));
      dispatch(setRequestResult({ ...data.data, access_token: token }));
      dispatch(getDeeplinkData(token));
      dispatch(setName((data.data.entity.name || data.data.entity.email).split('@')[0]));
      setAnalyticsUserId(data.data.entity.external_user_id);
    })
    .catch((error) => console.error(error));
};

export const setUuid = (userUuid) => (dispatch) => {
  const cookieUuid = Cookies.get('userUuid');

  if (!userUuid) {
    if (cookieUuid) {
      userUuid = cookieUuid;
    } else {
      userUuid = uuidv4();
    }
  }

  Cookies.set('userUuid', userUuid, { expires: 365 });
  dispatch(setUserUuid(userUuid));
  dispatch(setEventData({ id: 'userUuid', data: userUuid }));
  setAmplitudeUserProperties({ uuid: userUuid });
};

export const selectEmail = (state) => state.signup.email;
export const selectValidity = (state) => state.signup.isValid;
export const selectSuccess = (state) => state.signup.isSuccess;
export const selectToken = (state) => state.signup.token;
export const selectDeeplinkToken = (state) => state.signup.deeplinkToken;
export const selectDeeplinkUrl = (state) => state.signup.deeplinkUrl;
export const selectDeeplinkQR = (state) => state.signup.deeplinkQR;
export const selectUserID = (state) => state.signup.gzUserID;
export const selectUserName = (state) => state.signup.userName;
export const selectUserUuid = (state) => state.signup.userUuid;
export const selectStripePublishKey = (state) => state.signup.stripePublishKey;
export const selectStripeAccountName = (state) => state.signup.stripeAccountName;
export const selectBtToken = state => state.signup.btToken;
export const selectPaymentSettingsStatus = (state) => state.signup.paymentSettingsStatus;
export const selectPaymentProvider = state => state.signup.paymentProvider;

export default signupSlice.reducer
